import { Fragment, useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Tab, Transition } from "@headlessui/react";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { Button, Modal } from "src/components";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import { OrderInfoHomeScreen } from "./OrderInfoHomeScreen";
import OrderInfoFuid from "./OrderInfoFuid";
import OrderInfoInvoices from "./OrderInfoInvoices";
import OrderInfoApprovalHierarchy from "./OrderInfoApprovalHierarchy";
import OrderStatusTooltip from "../OrderDetailsTab/OrderStatusTooltip";
import { getOrderInfoHomeScreenData } from "../OrderDetailsTab/OrderHelper";
import ChangeOrderStatus from "./OrderInfoTabCards/ChangeOrderStatus";
import Comments from "../OrderDetailsTab/Comments";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { Book, MessageCircle, MessageSquareText } from "lucide-react";
import OrderInfoDetailsTab from "./OrderInfoDetailsTab";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import { toast } from "src/hooks/useToast";
import VersioningCrate from "src/components/Versioning/versioningModal.component";
import InvoiceUploadModal from "../OrdersHomeScreen/InvoiceUploadModal";  //M10-12287
import AddFeedbackForm from "src/pages/feedback/add-feedback-form.component";
import { addFeedback } from "src/services/feedback-api";

const OrderInfoScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When the user arrived here via the PO screen (PoOrderScreen passes
  // `autoOpenPoEdit: true` in navigate state), the Back button should return
  // to the PO tab (tabIndex 2 in OrdersHomePage) instead of the default
  // Counters tab.
  const cameFromPoScreen = (location?.state as { autoOpenPoEdit?: boolean } | null)?.autoOpenPoEdit === true;
  const backPath = cameFromPoScreen ? "/transactions/orders/2" : "/transactions/orders";
  const [isChangeOrderStatusModalOpen, setIsChangeOrderStatusModalOpen] = useState(false);
  const { orderDetailsFiltered, OrdersFiltersData } = useSelector((state: RootState) => state.ord);
  const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
  const [isVersioningModalOpen, setIsVersioningModalOpen] = useState<boolean>(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const { hasPermission: viewOrderDetailsPermission, loading: isViewPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.DETAILS_ALL.module,
    action: fineGrainedPermissionsKeys.ORDER.DETAILS_ALL.action,
  });
  const { hasPermission: viewFUIDPermission, loading: isFUIDPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.FUID.module,
    action: fineGrainedPermissionsKeys.ORDER.FUID.action,
  });
  const { hasPermission: viewInvoicesPermission, loading: isInvoicePermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.INVOICES.module,
    action: fineGrainedPermissionsKeys.ORDER.INVOICES.action,
  });
  const { hasPermission: viewHierarchyPermission, loading: isHierarchyPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.VIEWHIERARCHY.module,
    action: fineGrainedPermissionsKeys.ORDER.VIEWHIERARCHY.action,
  });

  const { hasPermission: viewRemarks } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.VIEWREMARKS.module,
    action: fineGrainedPermissionsKeys.ORDER.VIEWREMARKS.action,
  });

  const { hasPermission: hasPermissionUpdateOrderStatus } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.UPDATEORDERSTATUS.module,
    action: fineGrainedPermissionsKeys.ORDER.UPDATEORDERSTATUS.action,
  });


  const isPermissionLoading = isViewPermissionLoading || isFUIDPermissionLoading || isInvoicePermissionLoading || isHierarchyPermissionLoading

  const { tabIndex } = useParams();

  const ref = useOutsideClick(() => {
    setIsFiltersShown(false);
  });

  const refVersioning = useOutsideClick(() => {
    setIsVersioningModalOpen(false);
  });

  useEffect(() => {
    if (!isViewPermissionLoading && viewOrderDetailsPermission === false) {
      // redirect to home page
      navigate("/");
    }
  }, [isViewPermissionLoading, viewOrderDetailsPermission]);

  const tabs = [
    {
      label: "Order Info", condition: !isViewPermissionLoading && viewOrderDetailsPermission, component: <OrderInfoHomeScreen
        // fetchOrderDetails={() => fetchOrderId(orderCode as string)}
        orderInfo={getOrderInfoHomeScreenData(orderDetailsFiltered)}
      />
    },
    { label: "Order Details", condition: !isViewPermissionLoading && viewOrderDetailsPermission, component: <OrderInfoDetailsTab /> },
    { label: "FUID", condition: !isFUIDPermissionLoading && viewFUIDPermission, component: <OrderInfoFuid /> },
    { label: "Invoices", condition: !isInvoicePermissionLoading && viewInvoicesPermission, component: <OrderInfoInvoices /> },
    { label: "Approval Hierarchy", condition: !isHierarchyPermissionLoading && viewHierarchyPermission, component: <OrderInfoApprovalHierarchy /> },
  ]

  const filteredTabs = useMemo(() => {
    return (tabs || []).filter((tab) => tab.condition !== false);
  }, [tabs, isPermissionLoading]);

  const modalOpen = useSelector((state: RootState) => state.ord.fileUpload);  //M10-12287

  const handleFormSubmit = async (payload: any): Promise<boolean> => {
    setLoading(true);
    try {
      const res = await addFeedback(payload);

      if (res?.status === 200) {
        toast({ description: "Feedback submitted successfully" });
        return true;
      } else {
        toast({
          variant: "destructive",
          description: res?.data?.message || "Failed to submit feedback",
        });
        return false;
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.message || "Failed to submit feedback",
      });
      return false;
    } finally {
      setIsFeedbackModalOpen(false);
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="h-screen min-h-screen">
        {/* {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        ) :  */}
        <div className="h-full bg-white">
          {/* Header Section */}
          {/* <div className="p-6 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100"> */}
          <div className="mx-16 mt-6">
            <div className="container">
              <Button className="flex items-center px-0 py-2 text-lg text-[#7F56D9]" onClick={() => navigate(backPath)}>
                <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
                {cameFromPoScreen ? "Back to PO" : "Back to Orders"}
              </Button>

              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-[2rem] font-bold text-gray-800">{orderDetailsFiltered?.[0]?.order_code}</h1>
                  <Button
                    onClick={() => {
                      if (!hasPermissionUpdateOrderStatus) {
                        toast({ variant: "destructive", description: "You don't have permission to change order status" });
                        return;
                      }
                      setIsChangeOrderStatusModalOpen(true);
                    }}
                  >
                    <OrderStatusTooltip
                      orderStatusCode={orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id}
                      orderStatusText={orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_desc}
                    />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isVersioningModalOpen && (
            <div ref={refVersioning} className="px-3">
              <VersioningCrate orderId={Number(orderDetailsFiltered?.[0]?.order_id_int)} type="Order" />
            </div>
          )}

          {isFeedbackModalOpen && (
            <Modal isOpen={isFeedbackModalOpen} isHeader={true} title="Feedback" onCloseModal={() => setIsFeedbackModalOpen(false)}>
              <AddFeedbackForm
                onFormSubmit={handleFormSubmit}
                defaultValues={{ reference_number: orderDetailsFiltered?.[0]?.order_code, type: { name: "Orders", id: "order" } }}
                isLoading={loading}
              />
            </Modal>
          )}

          {!isPermissionLoading && <Tab.Group defaultIndex={Number(tabIndex) ?? 0}>
            <Tab.List className="border-b-[1px] relative shadow-md border-b-[rgba(0, 0, 0, 0.10)] mb-[1.16rem] px-[3.75rem] mt-4">
              {filteredTabs.map((item: any) => (
                <Tab key={item.label} as={Fragment}>
                  {({ selected }: { selected: boolean }) => (
                    <div
                      className={`relative inline-block cursor-pointer -bottom-[1px] px-4 py-3 font-semibold text-sm  border-b-2  not-italic  hover:border-b-[#586AF5] hover:text-[#586af5] focus:outline-none ${selected ? "text-[#586af5] border-b-[#586AF5]" : "text-[rgba(0,0,0,0.4)] border-b-transparent"
                        }`}
                    >
                      {item.label}
                    </div>
                  )}
                </Tab>
              ))}
              <MessageSquareText
                onClick={() => setIsFeedbackModalOpen(true)}
                className="text-gray-700 text-[24px] absolute  mt-[1.16rem] mr-[1.16rem] cursor-pointer top-0 right-16"
              />
              {viewRemarks && (
                <MessageCircle
                  onClick={() => setIsFiltersShown((prev) => !prev)}
                  className="text-gray-700 text-[24px] absolute  mt-[1.16rem] mr-[1.16rem] cursor-pointer top-0 right-0"
                />
              )}
              <Book
                onClick={() => setIsVersioningModalOpen(true)}
                className="text-gray-700 text-[24px] absolute  mt-[1.16rem] mr-[1.16rem] cursor-pointer top-0 right-8"
              />
            </Tab.List>

            <Tab.Panels>
              {filteredTabs.map((item: any) => (
                <Tab.Panel className="px-[3.75rem]" key={item.label}>
                  {item.component}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>}
        </div>
      </div>
      <Modal isOpen={isChangeOrderStatusModalOpen} isHeader={false} onCloseModal={() => setIsChangeOrderStatusModalOpen(false)}>
        <ChangeOrderStatus onCloseModal={() => setIsChangeOrderStatusModalOpen(false)} />
      </Modal>
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <Comments ref={ref} order_id={Number(orderDetailsFiltered?.[0]?.order_id_int) as number} uid={orderDetailsFiltered?.[0]?.uid} />
        </div>
      </Transition>
      {/* M10-12287 */}
      <InvoiceUploadModal
        modalOpen={modalOpen}
        company_code={OrdersFiltersData?.company?.find((item: any) => item.code == orderDetailsFiltered?.[0]?.company_code)}
        order_code={orderDetailsFiltered?.[0]?.order_code}
        module_code={orderDetailsFiltered?.[0]?.module_code}
      />
      {/* M10-12287 */}
    </div>
  );
};

export default OrderInfoScreen;
